import { useState, useEffect } from 'react';

export default function PaymentModal({ total, paymentMethod, onConfirm, onClose }) {
  const [cashGiven, setCashGiven] = useState('');
  const [cardDone, setCardDone] = useState(false);

  // Змішана оплата
  const [cashPart, setCashPart] = useState('');
  const [cardPart, setCardPart] = useState('');
  const [cardDoneMixed, setCardDoneMixed] = useState(false);
  const [mixedStep, setMixedStep] = useState('input'); // 'input' | 'card' | 'done'

  // Термінал (десктоп-каса): реальна оплата карткою через window.hardware.pay
  const hasTerminal = typeof window !== 'undefined' && !!(window.hardware && window.hardware.pay);
  const [payStatus, setPayStatus] = useState('idle');  // idle|waiting|approved|declined
  const [payResult, setPayResult] = useState(null);
  const [payError, setPayError] = useState('');

  async function runCardPayment(amount, onApproved) {
    if (!hasTerminal) { setPayStatus('manual'); return; }   // нема терміналу → ручне підтвердження
    setPayStatus('waiting'); setPayError('');
    try {
      const r = await window.hardware.pay(amount);
      if (r && r.approved) { setPayResult(r); setPayStatus('approved'); onApproved(); }
      else { setPayStatus('declined'); setPayError((r && (r.error || r.message)) || 'Оплату відхилено терміналом'); }
    } catch (e) { setPayStatus('declined'); setPayError(e.message || 'Помилка терміналу'); }
  }

  // Чиста оплата карткою — одразу відправляємо на термінал
  useEffect(() => {
    if (paymentMethod === 'card' && hasTerminal && payStatus === 'idle') {
      runCardPayment(total, () => setCardDone(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  // Термінал підтвердив оплату карткою → АВТОМАТИЧНО завершуємо продаж і друкуємо чек
  // (без зайвого кліку «Видати чек»). Невелика пауза — щоб касир побачив «✅ Оплату підтверджено».
  useEffect(() => {
    if (paymentMethod === 'card' && payStatus === 'approved' && payResult) {
      const t = setTimeout(() => onConfirm({ cash_given: total, change: 0, card_auth: payResult }), 700);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payStatus]);

  const change = cashGiven ? Math.max(0, parseFloat(cashGiven) - total).toFixed(2) : null;
  const cashValid = cashGiven && parseFloat(cashGiven) >= total;

  const quickAmounts = [...new Set([
    Math.ceil(total / 10) * 10,
    Math.ceil(total / 50) * 50,
    Math.ceil(total / 100) * 100,
    Math.ceil(total / 200) * 200,
  ])].filter(v => v >= total).slice(0, 4);

  // Змішана — авто розрахунок карткової частини.
  // ВАЖЛИВО: рахуємо в копійках (цілі числа), щоб не ловити похибку float
  // (напр. 4 + 55.01 = 59.010000000000005 і хибне «не вистачає 0.00»).
  const cents = v => Math.round((parseFloat(v) || 0) * 100);
  const cashPartNum = parseFloat(cashPart) || 0;
  const cardPartNum = parseFloat(cardPart) || 0;
  const totalC = cents(total);
  const mixedC = cents(cashPart) + cents(cardPart);
  const mixedSum = mixedC / 100;
  const mixedValid = mixedC >= totalC && cashPartNum >= 0 && cardPartNum > 0;
  const mixedChange = (Math.max(0, mixedC - totalC) / 100).toFixed(2);

  function handleCashPartChange(val) {
    setCashPart(val);
    const cash = parseFloat(val) || 0;
    const remaining = Math.max(0, total - cash);
    setCardPart(remaining > 0 ? remaining.toFixed(2) : '');
  }

  function handleCardPartChange(val) {
    setCardPart(val);
    const card = parseFloat(val) || 0;
    const remaining = Math.max(0, total - card);
    setCashPart(remaining > 0 ? remaining.toFixed(2) : '');
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>

        {/* ── ГОТІВКА ── */}
        {paymentMethod === 'cash' && (
          <>
            <div style={styles.header}>
              <h3 style={styles.title}>💵 Оплата готівкою</h3>
              <button style={styles.closeBtn} onClick={onClose}>✕</button>
            </div>
            <div style={styles.totalBox}>
              <span style={styles.totalLabel}>До оплати:</span>
              <span style={styles.totalAmount}>{total.toFixed(2)} ₴</span>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Сума від клієнта</label>
              <input style={styles.cashInput} type="number" step="0.01"
                placeholder="0.00" value={cashGiven} autoFocus
                onChange={e => setCashGiven(e.target.value)} />
            </div>
            <div style={styles.quickRow}>
              {quickAmounts.map(v => (
                <button key={v} style={styles.quickBtn} onClick={() => setCashGiven(String(v))}>
                  {v} ₴
                </button>
              ))}
              <button style={styles.quickBtn} onClick={() => setCashGiven(total.toFixed(2))}>
                Точно
              </button>
            </div>
            {cashGiven && (
              <div style={{...styles.changeRow, ...(cashValid ? styles.changeOk : styles.changeErr)}}>
                {cashValid ? (
                  <>
                    <span>💰 Решта:</span>
                    <span style={styles.changeAmount}>{change} ₴</span>
                  </>
                ) : (
                  <span>⚠️ Не вистачає {(total - parseFloat(cashGiven)).toFixed(2)} ₴</span>
                )}
              </div>
            )}
            <button
              style={{...styles.confirmBtn, ...(cashValid ? {} : styles.confirmBtnDisabled)}}
              disabled={!cashValid}
              onClick={() => onConfirm({ cash_given: parseFloat(cashGiven), change: parseFloat(change) })}>
              ✓ Провести оплату
            </button>
          </>
        )}

        {/* ── КАРТКА ── */}
        {paymentMethod === 'card' && (
          <>
            <div style={styles.header}>
              <h3 style={styles.title}>💳 Оплата карткою</h3>
              {!cardDone && <button style={styles.closeBtn} onClick={onClose}>✕</button>}
            </div>
            <div style={styles.totalBox}>
              <span style={styles.totalLabel}>До оплати:</span>
              <span style={styles.totalAmount}>{total.toFixed(2)} ₴</span>
            </div>
            {!cardDone ? (
              <>
                <div style={styles.terminalBox}>
                  {payStatus === 'declined' ? (
                    <>
                      <div style={styles.terminalIcon}>❌</div>
                      <p style={{...styles.terminalText, color:'#ff6b6b'}}>{payError}</p>
                      <button style={{...styles.confirmBtn, marginBottom:'8px'}}
                        onClick={() => runCardPayment(total, () => setCardDone(true))}>
                        ↻ Повторити оплату
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={styles.terminalIcon}>💳</div>
                      <p style={styles.terminalText}>{hasTerminal ? 'Очікування оплати на терміналі...' : 'Проведіть оплату на терміналі'}</p>
                      <div style={styles.loader}>
                        <div style={styles.loaderDot} />
                        <div style={{...styles.loaderDot, animationDelay:'0.2s'}} />
                        <div style={{...styles.loaderDot, animationDelay:'0.4s'}} />
                      </div>
                      <p style={styles.terminalHint}>Прикладіть картку або телефон</p>
                    </>
                  )}
                </div>
                <button style={styles.manualBtn} onClick={() => setCardDone(true)}>
                  Підтвердити вручну (оплата вже пройшла)
                </button>
              </>
            ) : (
              <>
                <div style={styles.successBox}>
                  <div style={styles.successIcon}>✅</div>
                  <p style={styles.successText}>Оплату підтверджено</p>
                  {payResult && payResult.approvalCode && (
                    <p style={styles.terminalHint}>
                      {payResult.paymentSystem || 'Картка'} {payResult.pan || ''} · код {payResult.approvalCode}
                    </p>
                  )}
                </div>
                <button style={styles.confirmBtn}
                  onClick={() => onConfirm({ cash_given: total, change: 0, card_auth: payResult })}>
                  ✓ Видати чек
                </button>
              </>
            )}
          </>
        )}

        {/* ── ЗМІШАНА ── */}
        {paymentMethod === 'mixed' && (
          <>
            <div style={styles.header}>
              <h3 style={styles.title}>🔀 Змішана оплата</h3>
              {mixedStep === 'input' && <button style={styles.closeBtn} onClick={onClose}>✕</button>}
            </div>
            <div style={styles.totalBox}>
              <span style={styles.totalLabel}>До оплати:</span>
              <span style={styles.totalAmount}>{total.toFixed(2)} ₴</span>
            </div>

            {mixedStep === 'input' && (
              <>
                <p style={styles.mixedHint}>Введіть скільки клієнт платить готівкою — решта автоматично карткою</p>
                <div style={styles.mixedRow}>
                  <div style={styles.mixedField}>
                    <label style={styles.label}>💵 Готівка (₴)</label>
                    <input style={styles.mixedInput} type="number" step="0.01" min="0"
                      placeholder="0.00" value={cashPart} autoFocus
                      onChange={e => handleCashPartChange(e.target.value)} />
                  </div>
                  <div style={styles.mixedPlus}>+</div>
                  <div style={styles.mixedField}>
                    <label style={styles.label}>💳 Картка (₴)</label>
                    <input style={styles.mixedInput} type="number" step="0.01" min="0"
                      placeholder="0.00" value={cardPart}
                      onChange={e => handleCardPartChange(e.target.value)} />
                  </div>
                </div>

                {mixedValid && (
                  <div style={styles.mixedSummary}>
                    <div style={styles.mixedSummaryRow}>
                      <span>Готівка:</span><span>{cashPartNum.toFixed(2)} ₴</span>
                    </div>
                    <div style={styles.mixedSummaryRow}>
                      <span>Картка:</span><span>{cardPartNum.toFixed(2)} ₴</span>
                    </div>
                    {parseFloat(mixedChange) > 0 && (
                      <div style={{...styles.mixedSummaryRow, color:'#5fbf8f', fontWeight:'bold'}}>
                        <span>Решта:</span><span>{mixedChange} ₴</span>
                      </div>
                    )}
                  </div>
                )}

                {!mixedValid && (cashPart || cardPart) && (
                  <div style={styles.mixedError}>
                    ⚠️ Сума {mixedSum.toFixed(2)} ₴ — не вистачає {(Math.max(0, totalC - mixedC) / 100).toFixed(2)} ₴
                  </div>
                )}

                <button
                  style={{...styles.confirmBtn, ...(mixedValid ? {} : styles.confirmBtnDisabled)}}
                  disabled={!mixedValid}
                  onClick={() => {
                    if (cardPartNum > 0) { setMixedStep('card'); runCardPayment(cardPartNum, () => setCardDoneMixed(true)); }
                    else onConfirm({ cash_given: cashPartNum, change: parseFloat(mixedChange) });
                  }}>
                  {cardPartNum > 0 ? '→ Перейти до оплати карткою' : '✓ Провести оплату'}
                </button>
              </>
            )}

            {mixedStep === 'card' && (
              <>
                <div style={styles.mixedCardInfo}>
                  <div style={styles.mixedCardRow}>
                    <span style={styles.mixedCardLabel}>Готівкою прийнято:</span>
                    <span style={styles.mixedCardVal}>{cashPartNum.toFixed(2)} ₴ ✓</span>
                  </div>
                  <div style={styles.mixedCardRow}>
                    <span style={styles.mixedCardLabel}>Карткою до оплати:</span>
                    <span style={{...styles.mixedCardVal, color:'#f5a623'}}>{cardPartNum.toFixed(2)} ₴</span>
                  </div>
                </div>

                {!cardDoneMixed ? (
                  <>
                    <div style={styles.terminalBox}>
                      {payStatus === 'declined' ? (
                        <>
                          <div style={styles.terminalIcon}>❌</div>
                          <p style={{...styles.terminalText, color:'#ff6b6b'}}>{payError}</p>
                          <button style={{...styles.confirmBtn, marginBottom:'8px'}}
                            onClick={() => runCardPayment(cardPartNum, () => setCardDoneMixed(true))}>
                            ↻ Повторити оплату
                          </button>
                        </>
                      ) : (
                        <>
                          <div style={styles.terminalIcon}>💳</div>
                          <p style={styles.terminalText}>Очікування {cardPartNum.toFixed(2)} ₴ на терміналі...</p>
                          <div style={styles.loader}>
                            <div style={styles.loaderDot} />
                            <div style={{...styles.loaderDot, animationDelay:'0.2s'}} />
                            <div style={{...styles.loaderDot, animationDelay:'0.4s'}} />
                          </div>
                        </>
                      )}
                    </div>
                    <button style={styles.manualBtn} onClick={() => setCardDoneMixed(true)}>
                      Підтвердити вручну (оплата вже пройшла)
                    </button>
                    <button style={{...styles.manualBtn, marginTop:'6px', color:'#9a8a6e', borderColor:'#9a8a6e'}}
                      onClick={() => { setMixedStep('input'); setPayStatus('idle'); }}>
                      ← Назад
                    </button>
                  </>
                ) : (
                  <>
                    <div style={styles.successBox}>
                      <div style={styles.successIcon}>✅</div>
                      <p style={styles.successText}>Оплату підтверджено</p>
                    </div>
                    <button style={styles.confirmBtn}
                      onClick={() => onConfirm({ cash_given: cashPartNum, change: parseFloat(mixedChange), card_auth: payResult })}>
                      ✓ Видати чек
                    </button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
      <style>{`@keyframes bounce{0%,80%,100%{transform:scale(0)}40%{transform:scale(1)}}`}</style>
    </div>
  );
}

const styles = {
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 },
  modal: { background:'#211c14', border:'1px solid #38301f', borderRadius:'16px', padding:'28px', width:'380px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' },
  title: { color:'#f5a623', margin:0, fontSize:'18px' },
  closeBtn: { background:'transparent', border:'none', color:'#9a8a6e', fontSize:'20px', cursor:'pointer' },
  totalBox: { display:'flex', justifyContent:'space-between', alignItems:'center', background:'#1c1812', borderRadius:'10px', padding:'14px 18px', marginBottom:'20px' },
  totalLabel: { color:'#9a8a6e', fontSize:'14px' },
  totalAmount: { color:'#f5a623', fontSize:'24px', fontWeight:'bold' },
  fieldGroup: { marginBottom:'12px' },
  label: { display:'block', color:'#9a8a6e', fontSize:'11px', marginBottom:'6px', textTransform:'uppercase' },
  cashInput: { width:'100%', padding:'12px 16px', borderRadius:'10px', border:'2px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'22px', textAlign:'center', boxSizing:'border-box', fontWeight:'bold' },
  quickRow: { display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' },
  quickBtn: { flex:1, minWidth:'70px', padding:'8px', background:'#38301f', color:'#6cb6ff', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'bold' },
  changeRow: { padding:'12px 16px', borderRadius:'10px', marginBottom:'16px', display:'flex', justifyContent:'space-between', alignItems:'center' },
  changeOk: { background:'#1a3a2a', border:'1px solid #2d6a4f' },
  changeErr: { background:'#3a1a1a', border:'1px solid #ff6b6b', color:'#ff6b6b', fontSize:'13px' },
  changeAmount: { fontSize:'22px', fontWeight:'bold', color:'#5fbf8f' },
  confirmBtn: { width:'100%', padding:'14px', background:'#2d6a4f', color:'#5fbf8f', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'15px', fontWeight:'bold' },
  confirmBtnDisabled: { background:'#38301f', color:'#9a8a6e', cursor:'not-allowed' },
  terminalBox: { textAlign:'center', padding:'16px 0' },
  terminalIcon: { fontSize:'40px', marginBottom:'10px' },
  terminalText: { color:'#fff', fontSize:'15px', marginBottom:'14px' },
  loader: { display:'flex', gap:'8px', justifyContent:'center', marginBottom:'14px' },
  loaderDot: { width:'10px', height:'10px', background:'#f5a623', borderRadius:'50%', animation:'bounce 1.4s infinite ease-in-out' },
  terminalHint: { color:'#9a8a6e', fontSize:'12px' },
  manualBtn: { width:'100%', padding:'11px', background:'transparent', color:'#f5a623', border:'1px solid #f5a623', borderRadius:'10px', cursor:'pointer', fontSize:'14px', marginTop:'8px' },
  successBox: { textAlign:'center', padding:'16px 0' },
  successIcon: { fontSize:'40px', marginBottom:'10px' },
  successText: { color:'#5fbf8f', fontSize:'17px', fontWeight:'bold', marginBottom:'16px' },
  mixedHint: { color:'#9a8a6e', fontSize:'13px', marginBottom:'14px', marginTop:0 },
  mixedRow: { display:'flex', alignItems:'flex-end', gap:'10px', marginBottom:'14px' },
  mixedField: { flex:1 },
  mixedInput: { width:'100%', padding:'10px 12px', borderRadius:'8px', border:'2px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'18px', textAlign:'center', boxSizing:'border-box', fontWeight:'bold' },
  mixedPlus: { color:'#9a8a6e', fontSize:'20px', paddingBottom:'10px', flexShrink:0 },
  mixedSummary: { background:'#1c1812', borderRadius:'10px', padding:'12px 16px', marginBottom:'16px' },
  mixedSummaryRow: { display:'flex', justifyContent:'space-between', fontSize:'14px', marginBottom:'4px' },
  mixedError: { color:'#ff6b6b', fontSize:'13px', textAlign:'center', marginBottom:'14px' },
  mixedCardInfo: { background:'#1c1812', borderRadius:'10px', padding:'14px 16px', marginBottom:'16px' },
  mixedCardRow: { display:'flex', justifyContent:'space-between', marginBottom:'6px' },
  mixedCardLabel: { color:'#9a8a6e', fontSize:'13px' },
  mixedCardVal: { fontWeight:'bold', fontSize:'14px' },
};
